import { AloftPrice, ManualAdjustments, orUndefined } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectDateOfSaleOrListToSaleAdjustments } from '../calculatedMarketAdjustmentSelectors/selectDateOfSaleOrListToSaleAdjustment.ts';
import { selectCompSequence } from '../orderSelectors.ts';
import { selectManualAdjustmentsEntities } from '../propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { selectADUAdjs } from './selectADUAdj.ts';
import { selectADUFullBathsAdjs } from './selectADUFullBathsAdj.ts';
import { selectADUHalfBathsAdjs } from './selectADUHalfBathsAdj.ts';
import { selectActualAgeAdjs } from './selectActualAgeAdj.ts';
import { selectAreaAdjs } from './selectAreaAdj.ts';
import { selectAtticAdjs } from './selectAtticAdjustment.ts';
import { selectBarnAdjs } from './selectBarnAdj.ts';
import { selectBedsAdjs } from './selectBedsAdj.ts';
import { selectBelowGradeBedroomsAdjs } from './selectBelowGradeBedroomsAdj.ts';
import { selectBelowGradeFullBathsAdjs } from './selectBelowGradeFullBathsAdj.ts';
import { selectBelowGradeHalfBathsAdjs } from './selectBelowGradeHalfBathsAdj.ts';
import { selectCarportAdjs } from './selectCarportAdj.ts';
import { selectConditionAdjs } from './selectConditionAdj.ts';
import { selectDeckAdjs } from './selectDeckAdj.ts';
import { selectDetachedGarageAdjs } from './selectDetachedGarageAdj.ts';
import { selectExternalObsolescenceAdjs } from './selectExternalObsolescenceAdj.ts';
import { selectFinishedBasementAreaAdjs } from './selectFinishedBasementAreaAdj.ts';
import { selectFireplaceAdjs } from './selectFireplaceAdjustment.ts';
import { selectFullBathsAdjs } from './selectFullBathsAdj.ts';
import { selectFunctionalUtilityAdjs } from './selectFunctionalUtilityAdj.ts';
import { selectGarageAdjs } from './selectGarageAdj.ts';
import { selectHalfBathsAdjs } from './selectHalfBathsAdj.ts';
import { selectManualCostAdjs } from './selectManualCostAdj.ts';
import { selectNumberOfUnitsAdjs } from './selectNumberOfUnitsAdj.ts';
import { selectOtherAdjustments } from './selectOtherAdjustments.ts';
import { selectPatioAdjs } from './selectPatioAdj.ts';
import { selectPoolAdjs } from './selectPoolAdj.ts';
import { selectPorchAdjs } from './selectPorchAdj.ts';
import { selectQualityAdjs } from './selectQualityAdj.ts';
import { selectShedAdjs } from './selectShedAdj.ts';
import { selectSiteAdjs } from './selectSiteAdj.ts';
import { selectSpaAdjs } from './selectSpaAdj.ts';
import { selectTotalBasementAreaAdjs } from './selectTotalBasementAreaAdj.ts';
import { selectTotalCostToCureAdjs } from './selectTotalCostToCureAdj.ts';
import { selectWorkshopAdjs } from './selectWorkshopAdj.ts';

export type Adjustments = {
  condition?: AloftPrice;
  quality?: AloftPrice;

  site?: AloftPrice;
  siteView?: AloftPrice;
  siteInfluence?: AloftPrice;
  basementFullBaths?: AloftPrice;
  basementHalfBaths?: AloftPrice;
  basementBedrooms?: AloftPrice;
  aduFullBaths?: AloftPrice;
  aduHalfBaths?: AloftPrice;
  basementTotal?: AloftPrice;
  basementFinished?: AloftPrice;
  gla?: AloftPrice;
  numberOfUnits?: AloftPrice;

  fullBaths?: AloftPrice;
  halfBaths?: AloftPrice;
  bathAboveGrade?: AloftPrice;

  /** @deprecated - Use bathBelowGrade instead */
  belowGradeFullBaths?: AloftPrice;
  /** @deprecated - Use bathBelowGrade instead */
  belowGradeHalfBaths?: AloftPrice;
  bathBelowGrade?: AloftPrice;

  bedrooms?: AloftPrice;
  actualAge?: AloftPrice;

  /** @deprecated - Use garageCarport instead */
  garage?: AloftPrice;
  /** @deprecated - Use garageCarport instead */
  carport?: AloftPrice;
  /** @deprecated - Use garageCarport instead */
  detachedGarage?: AloftPrice;

  garageCarport?: AloftPrice;

  /** @deprecated - Use basement instead */
  finishedBasementArea?: AloftPrice;
  /** @deprecated - Use basement instead */
  totalBasementArea?: AloftPrice;
  basement?: AloftPrice;

  /** @deprecated - Use poolSpa instead */
  pool?: AloftPrice;
  /** @deprecated - Use poolSpa instead */
  spa?: AloftPrice;
  poolSpa?: AloftPrice;

  /** @deprecated - Use porchPatioDeck instead */
  porch?: AloftPrice;
  /** @deprecated - Use porchPatioDeck instead */
  patio?: AloftPrice;
  /** @deprecated - Use porchPatioDeck instead */
  deck?: AloftPrice;
  porchPatioDeck?: AloftPrice;

  adu?: AloftPrice;
  attic?: AloftPrice;
  barn?: AloftPrice;
  fireplace?: AloftPrice;
  manualCost?: AloftPrice;
  shed?: AloftPrice;
  workshop?: AloftPrice;
  solarPanel?: AloftPrice;

  totalCostToCure?: AloftPrice;
  other?: AloftPrice;

  functionalUtility?: AloftPrice;
  externalObsolescence?: AloftPrice;

  // Market Adjustments
  dateOfSale?: AloftPrice;
  termsOfSale?: AloftPrice;
  concessions?: AloftPrice;
};

const getAdjustments = (
  barn?: AloftPrice,
  carport?: AloftPrice,
  condition?: AloftPrice,
  porch?: AloftPrice,
  patio?: AloftPrice,
  deck?: AloftPrice,
  finishedBasementArea?: AloftPrice,
  fullBaths?: AloftPrice,
  beds?: AloftPrice,
  actualAge?: AloftPrice,
  garage?: AloftPrice,
  detachedGarage?: AloftPrice,
  halfBaths?: AloftPrice,
  belowGradeFullBaths?: AloftPrice,
  belowGradeHalfBaths?: AloftPrice,
  belowGradeBedrooms?: AloftPrice,
  pool?: AloftPrice,
  quality?: AloftPrice,
  area?: AloftPrice,
  numberOfUnits?: AloftPrice,
  shed?: AloftPrice,
  site?: AloftPrice,
  spa?: AloftPrice,
  totalBasementArea?: AloftPrice,
  workshop?: AloftPrice,
  attic?: AloftPrice,
  fireplace?: AloftPrice,
  manualCost?: AloftPrice,
  adu?: AloftPrice,
  aduFullBaths?: AloftPrice,
  aduHalfBaths?: AloftPrice,
  functionalUtility?: AloftPrice,
  externalObsolescence?: AloftPrice,
  dateOfSale?: AloftPrice,
  totalCostToCure?: AloftPrice,
  other?: AloftPrice,
  manualAdjustments?: ManualAdjustments,
): Adjustments => {
  const bathAboveGrade = AloftPrice.addWithNulls(fullBaths, halfBaths) ?? undefined;
  const basement = AloftPrice.addWithNulls(finishedBasementArea, totalBasementArea) ?? undefined;
  const garageCarport = AloftPrice.addWithNulls(carport, detachedGarage, garage) ?? undefined;
  const porchPatioDeck = AloftPrice.addWithNulls(deck, porch, patio) ?? undefined;
  const poolSpa = AloftPrice.addWithNulls(pool, spa) ?? undefined;
  const bathBelowGrade =
    AloftPrice.addWithNulls(belowGradeFullBaths, belowGradeHalfBaths) ?? undefined;

  return {
    condition,
    quality,
    site,

    gla: area,
    numberOfUnits,

    fullBaths,
    halfBaths,
    bathAboveGrade,

    belowGradeFullBaths,
    belowGradeHalfBaths,
    basementFullBaths: belowGradeFullBaths,
    basementHalfBaths: belowGradeHalfBaths,
    basementBedrooms: belowGradeBedrooms,
    basementFinished: finishedBasementArea,
    basementTotal: totalBasementArea,
    bathBelowGrade,

    garage,
    detachedGarage,
    carport,
    garageCarport,

    finishedBasementArea,
    totalBasementArea,
    basement,

    pool,
    spa,
    poolSpa,

    porch,
    patio,
    deck,
    porchPatioDeck,

    adu,
    aduFullBaths,
    aduHalfBaths,
    attic,
    barn,
    bedrooms: beds,
    actualAge,
    fireplace,
    manualCost,
    shed,
    workshop,

    totalCostToCure,
    other,

    functionalUtility,
    externalObsolescence,

    dateOfSale,
    termsOfSale: orUndefined(AloftPrice.schema.parse)(manualAdjustments?.termsOfSale),
    concessions: orUndefined(AloftPrice.schema.parse)(manualAdjustments?.concessions),
  };
};

export const selectAdjustments = createSelector(
  [
    selectBarnAdjs,
    selectCarportAdjs,
    selectConditionAdjs,
    selectPorchAdjs,
    selectPatioAdjs,
    selectDeckAdjs,
    selectFinishedBasementAreaAdjs,
    selectFullBathsAdjs,
    selectBedsAdjs,
    selectActualAgeAdjs,
    selectGarageAdjs,
    selectDetachedGarageAdjs,
    selectHalfBathsAdjs,
    selectBelowGradeFullBathsAdjs,
    selectBelowGradeHalfBathsAdjs,
    selectBelowGradeBedroomsAdjs,
    selectPoolAdjs,
    selectQualityAdjs,
    selectAreaAdjs,
    selectNumberOfUnitsAdjs,
    selectShedAdjs,
    selectSiteAdjs,
    selectSpaAdjs,
    selectTotalBasementAreaAdjs,
    selectWorkshopAdjs,
    selectAtticAdjs,
    selectFireplaceAdjs,
    selectManualCostAdjs,
    selectADUAdjs,
    selectADUFullBathsAdjs,
    selectADUHalfBathsAdjs,
    selectFunctionalUtilityAdjs,
    selectExternalObsolescenceAdjs,
    selectDateOfSaleOrListToSaleAdjustments,
    selectTotalCostToCureAdjs,
    selectOtherAdjustments,
    selectManualAdjustmentsEntities,
    selectCompSequence,
  ],
  (
    barnAdjs,
    carportAdjs,
    conditionAdjs,
    porchAdjs,
    patioAdjs,
    deckAdjs,
    finishedBasementAreaAdjs,
    fullBathsAdjs,
    bedsAdjs,
    actualAgeAdjs,
    garageAdjs,
    detachedGarageAdjs,
    halfBathsAdjs,
    belowGradeFullBathAdjs,
    belowGradeHalfBathAdjs,
    belowGradeBedroomsAdjs,
    poolAdjs,
    qualityAdjs,
    areaAdjs,
    numberOfUnitAdjs,
    shedAdjs,
    siteAdjs,
    spaAdjs,
    totalBasementAreaAdjs,
    workshopAdjs,
    atticAdjs,
    fireplaceAdjs,
    manualCostAdjs,
    aduAdjs,
    aduFullBathsAdjs,
    aduHalfBathsAdjs,
    functionalUtilityAdjs,
    externalObsolescenceAdjs,
    dateOfSaleAdjs,
    totalCostToCureAdjs,
    otherAdjs,
    manualAdjustments,
    compSequence,
  ) =>
    compSequence.reduce((acc: Record<string, Adjustments>, propID) => {
      const adjs = getAdjustments(
        barnAdjs[propID],
        carportAdjs[propID],
        conditionAdjs[propID],
        porchAdjs[propID],
        patioAdjs[propID],
        deckAdjs[propID],
        finishedBasementAreaAdjs[propID],
        fullBathsAdjs[propID],
        bedsAdjs[propID],
        actualAgeAdjs[propID],
        garageAdjs[propID],
        detachedGarageAdjs[propID],
        halfBathsAdjs[propID],
        belowGradeFullBathAdjs[propID],
        belowGradeHalfBathAdjs[propID],
        belowGradeBedroomsAdjs[propID],
        poolAdjs[propID],
        qualityAdjs[propID],
        areaAdjs[propID],
        numberOfUnitAdjs[propID],
        shedAdjs[propID],
        siteAdjs[propID],
        spaAdjs[propID],
        totalBasementAreaAdjs[propID],
        workshopAdjs[propID],
        atticAdjs[propID],
        fireplaceAdjs[propID],
        manualCostAdjs[propID],
        aduAdjs[propID],
        aduFullBathsAdjs[propID],
        aduHalfBathsAdjs[propID],
        functionalUtilityAdjs[propID],
        externalObsolescenceAdjs[propID],
        dateOfSaleAdjs[propID],
        totalCostToCureAdjs[propID],
        otherAdjs[propID],
        manualAdjustments[propID],
      );

      if (adjs) acc[propID] = adjs;
      return acc;
    }, {}),
);
